import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';
import { FOCUS_RING } from '@/lib/focus';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Persistent guidance. Never a placeholder -- placeholders vanish on focus. */
  hint?: string;
  error?: string;
}

export const Field = forwardRef<HTMLInputElement, Props>(function Field(
  { label, hint, error, className, id, required, ...rest },
  ref,
) {
  const generated = useId();
  const inputId = id ?? generated;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  return (
    <div className="flex flex-col gap-2">
      {/* Visible label, always. A placeholder-as-label disappears the moment
          the user starts typing, which is exactly when they need it. */}
      <label htmlFor={inputId} className="text-small font-medium text-text-secondary">
        {label}
        {required && (
          <span className="ml-1 text-danger" aria-hidden>
            *
          </span>
        )}
      </label>

      <input
        ref={ref}
        id={inputId}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(hint && hintId, error && errorId) || undefined}
        className={cn(
          'min-h-touch rounded border bg-surface-1 px-3 text-body text-text',
          'placeholder:text-text-tertiary',
          'transition-colors duration-press',
          FOCUS_RING,
          error ? 'border-danger' : 'border-border focus:border-accent-border',
          className,
        )}
        {...rest}
      />

      {hint && !error && (
        <p id={hintId} className="text-small text-text-tertiary">
          {hint}
        </p>
      )}

      {/* Error sits next to the field it belongs to, not in a summary at the
          top where the user has to map it back themselves. role=alert so it is
          announced when it appears. */}
      {error && (
        <p id={errorId} role="alert" className="text-small text-danger">
          {error}
        </p>
      )}
    </div>
  );
});
